import {
    Component,
    Inject,
    OnInit,
    OnDestroy,
    ViewChild,
    ElementRef,
    TemplateRef,
    HostListener,
    AfterViewInit
} from '@angular/core';
import { AppStartupService, BaseComponent, DisruptionMessage } from '@ridango/core';
import {
    ApplicationFacade,
    Departure,
    DetailedRoute,
    DetailedStop,
    JourneyDirection,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    Time,
    TripClickEvent
} from '@ridango/components';
import { ActivatedRoute, Router } from '@angular/router';
import { MapApiService } from '../../map/services';
import { AuthService, DepartureArrivalTime, LatLng } from '@ridango/core';
import { defaultIfEmpty, switchMap, take, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { FavoritesFacade } from '@ridango/components/lib/favorites';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, timer } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { AddFavoriteStopModalComponent } from './add-favorite-stop-modal/add-favorite-stop-modal.component';
import { Temporal } from '@js-temporal/polyfill';
import { IonModalService } from '@ridango/core/lib/services/ion-modal.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Capacitor } from '@capacitor/core';
import { SmallScreenSheetModalContainerService } from '../../small-screen-sheet-modal-container/small-screen-sheet-modal-container.service';

@Component({
    selector: 'rwidget-stop-details',
    templateUrl: './stop-details.component.html',
    styleUrls: ['./stop-details.component.scss']
})
export class StopDetailsComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('favoriteButton', { read: ElementRef }) favoriteButton: ElementRef;
    @ViewChild('dummyFocus') dummyFocus!: ElementRef;
    @ViewChild('stickyHeader') stickyHeader: TemplateRef<any>;
    readonly DEPARTURES_SEARCH_RANGE_SECONDS = 60 * 60 * 24;
    private timezone: string = this.appStartupService.getTimezone();

    isLoadingStop = true;
    assetsPath$ = this.applicationFacade.assetsPath$;
    location$ = this.openTripPlannerFacade.location$;
    isFavorite: boolean;

    stopId: string;
    stop: DetailedStop;
    departures: Departure[] = [];
    filteredDepartures: Departure[] = [];
    routeFilterOptions: DetailedRoute[] = [];
    departTimeFilter: DepartureArrivalTime;
    routeNameFilter: string[] = [];
    favoritesStops = [];
    disruptionMessages: DisruptionMessage[] = [];
    numberOfDeparturesInList = this.env.EXPLORE?.NUMBER_OF_DEPARTURES_IN_LIST || 3;
    isIos = Capacitor.getPlatform() === 'ios';

    private isFavoriteModalOpened = false;
    isLoadingDepartures: boolean;

    initialWindowWidth: number = window.innerWidth;
    isMobileView: boolean = window.innerWidth < 768;

    @HostListener('window:resize', ['$event'])
    onResize(_event?: Event) {
        if (this.initialWindowWidth !== window.innerWidth) {
            this.initialWindowWidth = window.innerWidth;
            this.isMobileView = window.innerWidth < 768;
        }
    }

    constructor(
        private applicationFacade: ApplicationFacade,
        private route: ActivatedRoute,
        private otpService: OpenTripPlannerService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private favoritesFacade: FavoritesFacade,
        private openTripPlannerService: OpenTripPlannerService,
        private mapApi: MapApiService,
        private router: Router,
        private translate: TranslateService,
        @Inject('ENV') public env: Env,
        public authService: AuthService,
        private modalCtrl: ModalController,
        private appStartupService: AppStartupService,
        private ionModalService: IonModalService,
        private liveAnnouncer: LiveAnnouncer,
        private smallScreenSheetModalContainerService: SmallScreenSheetModalContainerService
    ) {
        super();
    }

    ngOnInit(): void {
        if (!this.departTimeFilter?.datetime) {
            this.departTimeFilter = {
                datetime: Temporal.Now.plainDateTimeISO(this.timezone).toString()
            };
        }

        combineLatest([this.route.queryParamMap, this.route.paramMap])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([queryParams, params]) => {
                const filterDateTime = queryParams.get('filterDateTime');
                const filterType = queryParams.get('filterType');

                if (this.stopId && this.stopId !== params.get('stopId')) {
                    this.departTimeFilter = {
                        datetime: Temporal.Now.plainDateTimeISO(this.timezone).toString()
                    };
                }

                this.stopId = params.get('stopId');

                if (filterDateTime) {
                    const date = Temporal.PlainDateTime.from(filterDateTime);

                    if (Temporal.Now.instant().epochSeconds < date.toZonedDateTime(this.timezone).epochSeconds) {
                        this.departTimeFilter = {
                            ...this.departTimeFilter,
                            datetime: date.toString()
                        };
                    }
                }

                if (filterType) {
                    this.departTimeFilter = {
                        ...this.departTimeFilter,
                        type: filterType as 'leaveAfter' | 'arriveBy'
                    };
                }

                this.getData();
            });

        if (this.env.EXPLORE?.SHOW_FAVORITE_STOPS) {
            this.favoritesFacade.getFavoriteStops();

            this.favoritesFacade.favoriteStops$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((favoritesStops) => {
                    this.favoritesStops = favoritesStops;
                    this.isFavorite = !!this.favoritesStops.find((s) => s.stopId === this.stopId);
                });
        }
    }

    ngAfterViewInit() {
        if (this.stickyHeader) {
            this.smallScreenSheetModalContainerService.setHeaderTemplate(this.stickyHeader);
        }
    }

    navigateBack() {
        this.router.navigate([this.translate.currentLang, 'explore']);
        const breakpoints = this.ionModalService.getBreakpoints();
        if (breakpoints?.length > 0) {
            this.ionModalService.setCurrentBreakpoint(breakpoints[0]);
        }
    }

    async favoriteClickHandle() {
        if (this.isFavorite) {
            this.favoritesFacade.deleteFavoriteStop(this.stop.id);
            this.liveAnnouncer.announce(this.translate.instant('FAVORITE_REMOVED'), 'assertive');
        } else {
            if (this.isFavoriteModalOpened) {
                return;
            }

            this.isFavoriteModalOpened = true;
            const modal = await this.modalCtrl.create({
                component: AddFavoriteStopModalComponent,
                cssClass: 'add-favorite-stop-modal',
                componentProps: {
                    stop: this.stop
                }
            });
            await modal.present();
            const { role } = await modal.onWillDismiss();
            if (role === 'confirm') {
                this.isFavoriteModalOpened = false;
                this.liveAnnouncer.announce(this.translate.instant('FAVORITE_ADDED')).then(() => {
                    if (this.isIos) {
                        // Adjust timing if necessary
                        timer(2500).subscribe(() => {
                            this.favoriteButton.nativeElement.focus(); // Refocus on the button so VoiceOver reads the new state
                        });
                    }
                });
            } else {
                this.isFavoriteModalOpened = false;
            }
        }
    }

    async scheduleClickHandle() {
        this.location$.pipe(take(1)).subscribe((location) => {
            if (location.coordinates) {
                this.openTripPlannerFacade.setSearchAddress(
                    JourneyDirection.Origin,
                    this.translate.instant('CURRENT_LOCATION'),
                    location.coordinates,
                    true
                );
            } else {
                this.openTripPlannerFacade.setSearchAddress(JourneyDirection.Origin, null, null, false);
            }
        });
        this.openTripPlannerService
            .getStop(this.stopId, 'stops')
            .pipe(take(1))
            .subscribe((stop) => {
                const latLng: LatLng = { lat: stop.lat, lng: stop.lon };
                this.openTripPlannerFacade.setSearchAddress(JourneyDirection.Destination, stop.name, latLng, false);
            });
        await this.router.navigate([this.translate.currentLang, 'journey-planner', 'result']);
    }

    handleRouteFilterChange(routeShortNames: string[]) {
        if (this.sameRouteNameFilter(this.routeNameFilter, routeShortNames)) {
            return;
        }
        this.routeNameFilter = routeShortNames;
        this.updateFilteredDepartures();
    }

    private sameRouteNameFilter(a: string[], b: string[]): boolean {
        if (a.length !== b.length) {
            return false;
        }
        const sa = [...a].sort();
        const sb = [...b].sort();
        return sa.every((v, i) => v === sb[i]);
    }

    private updateFilteredDepartures(): void {
        const milliseconds = Temporal.Now.instant().epochMilliseconds;

        const departures = this.departures
            .filter((d) => this.routeNameFilter.includes(d.route.shortName))
            .filter((d) => {
                if (typeof d.time.scheduledDeparture === 'string') {
                    return (
                        this.getZonedMilliseconds(d.time.realtimeDeparture || d.time.scheduledDeparture) > milliseconds
                    );
                }

                return this.getScheduledDepartureTime(d.time) > milliseconds;
            });
        if (this.routeNameFilter.length === this.routeFilterOptions.length) {
            this.filteredDepartures = departures;
        } else {
            this.filteredDepartures = departures
                .filter((d) => this.routeNameFilter.includes(d.route.shortName))
                .slice(0, this.env?.EXPLORE?.MAX_DEPARTURES_WITH_SELECTED_ROUTE_FILTER ?? departures.length);
        }
    }

    async openTripDetailsView(departure: Departure) {
        const fromTime = Temporal.Instant.from(departure.time?.scheduledArrival.toString())
            .toZonedDateTime({ timeZone: this.timezone, calendar: 'iso8601' })
            .epochSeconds.toString();

        const tripViewParams: TripClickEvent = {
            tripId: departure.time.tripId,
            userDepartStopId: this.stop.id,
            routeId: departure.route.id,
            fromTime,
            shortName: this.env.EXPLORE?.SELECTED_BUS_STOPPED ? departure.route.shortName : null
        };

        await this.router
            .navigate([], {
                queryParams: {
                    filterDateTime: this.departTimeFilter.datetime,
                    filterType: this.departTimeFilter.type
                },
                queryParamsHandling: 'merge',
                replaceUrl: true
            })
            .then(() => {
                this.router.navigate([`trip`], { relativeTo: this.route.parent, queryParams: tripViewParams });
            });
    }

    getData() {
        if (!this.stopId) {
            return;
        }

        this.clampDepartTimeFilterToNowIfPast();

        if (this.env.EXPLORE?.STOP_VIEW?.SHOW_DEPART_ARRIVE_TOGGLE) {
            this.departTimeFilter = {
                ...this.departTimeFilter,
                type: 'leaveAfter'
            };
        }

        this.isLoadingStop = true;
        this.isLoadingDepartures = true;
        const fromTime = Temporal.PlainDateTime.from(this.departTimeFilter.datetime).toZonedDateTime(
            this.timezone
        ).epochSeconds;

        const stop$ = this.openTripPlannerFacade.location$.pipe(
            take(1),
            switchMap((location) => {
                if (location?.coordinates) {
                    const currentPos: LatLng = {
                        lat: location.coordinates?.lat,
                        lng: location.coordinates?.lng
                    };
                    return this.otpService.getStopWithWalkingDistance(this.stopId, currentPos, fromTime);
                }
                return this.otpService.getStop(this.stopId, 'stops');
            })
        );

        const departures$ = this.otpService
            .getUpcomingDepartures({
                stopId: this.stopId,
                timeRangeSeconds: this.DEPARTURES_SEARCH_RANGE_SECONDS,
                fromTime
            })
            .pipe(defaultIfEmpty([]));

        forkJoin({ stop: stop$, departures: departures$, params: this.route.queryParams.pipe(take(1)) })
            .pipe(defaultIfEmpty(null), takeUntil(this.componentDestroyed$))
            .subscribe((data) => {
                if (!data) {
                    this.isLoadingStop = false;
                    this.departures = [];
                    this.routeFilterOptions = [];
                    this.routeNameFilter = [];
                    this.filteredDepartures = [];
                    return;
                }
                this.stop = data.stop;

                if (data.stop.disruptionMessages?.length > 0) {
                    try {
                        const dateTime = Temporal.PlainDateTime.from(this.departTimeFilter.datetime).toZonedDateTime(
                            this.timezone
                        );
                        this.disruptionMessages = data.stop.disruptionMessages?.filter(
                            (message: DisruptionMessage) =>
                                Temporal.ZonedDateTime.compare(
                                    Temporal.ZonedDateTime.from(`${message.activePeriodStart}[${this.timezone}]`),
                                    dateTime
                                ) <= 0 &&
                                Temporal.ZonedDateTime.compare(
                                    dateTime,
                                    Temporal.ZonedDateTime.from(`${message.activePeriodEnd}[${this.timezone}]`)
                                ) === -1
                        );
                    } catch (e) {
                        console.error('Error parsing disruption messages', e);
                    }
                } else {
                    this.disruptionMessages = [];
                }

                this.openTripPlannerFacade.setSelectedStop(this.stop);
                this.departures = data.departures;
                this.setRouteFilterOptions();
                this.isLoadingStop = false;
                this.isLoadingDepartures = false;

                if (data.params.centering) {
                    this.setMapStopsAndCenterMap();
                }

                this.isFavorite = !!this.favoritesStops.find((s) => s.stopId === this.stopId);
            });
    }

    refreshData() {
        this.clampDepartTimeFilterToNowIfPast();
        this.isLoadingDepartures = true;
        const fromTime = Temporal.PlainDateTime.from(this.departTimeFilter.datetime).toZonedDateTime(
            this.timezone
        ).epochSeconds;

        this.otpService
            .getUpcomingDepartures({
                stopId: this.stopId,
                timeRangeSeconds: this.DEPARTURES_SEARCH_RANGE_SECONDS,
                fromTime
            })
            .pipe(defaultIfEmpty([]))
            .subscribe((departures) => {
                this.departures = departures;
                this.updateFilteredDepartures();
                this.isLoadingDepartures = false;
            });
    }

    private clampDepartTimeFilterToNowIfPast(): void {
        const now = Temporal.Now.plainDateTimeISO(this.timezone).toString();
        const datetime = this.departTimeFilter?.datetime;
        let isInPast = true;

        if (datetime) {
            try {
                isInPast =
                    Temporal.PlainDateTime.from(datetime).toZonedDateTime(this.timezone).epochSeconds <
                    Temporal.Now.instant().epochSeconds;
            } catch {
                // Invalid datetime string - treat as past
            }
        }

        if (isInPast) {
            this.departTimeFilter = { ...this.departTimeFilter, datetime: now };
        }
    }

    private setMapStopsAndCenterMap() {
        this.mapApi
            .mapReady()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((ready) => {
                if (ready) {
                    const latLng: LatLng = { lat: this.stop.lat, lng: this.stop.lon };
                    this.mapApi.centerMapToCoordinates(latLng);
                    this.openTripPlannerFacade.setExploreMapStops([this.stop]);
                }
            });
    }

    private setRouteFilterOptions() {
        const allRoutes = this.departures.map((d) => d.route);

        const alreadyAddedShortNames = [];
        const uniqueRoutes = [];
        allRoutes.forEach((route) => {
            if (!alreadyAddedShortNames.includes(route.shortName)) {
                alreadyAddedShortNames.push(route.shortName);
                uniqueRoutes.push(route);
            }
        });

        this.routeFilterOptions = uniqueRoutes.sort((a, b) => {
            const aName = Number(a.shortName) ? Number(a.shortName) : a.shortName;
            const bName = Number(b.shortName) ? Number(b.shortName) : b.shortName;
            return aName > bName ? 1 : -1;
        });
        const allShortNames = this.routeFilterOptions.map((r) => r.shortName);
        if (!this.sameRouteNameFilter(this.routeNameFilter, allShortNames)) {
            this.routeNameFilter = allShortNames;
        }
        this.updateFilteredDepartures();
    }

    ngOnDestroy() {
        this.smallScreenSheetModalContainerService.setHeaderTemplate(null);
        super.ngOnDestroy();
        this.openTripPlannerFacade.setSelectedStop(null);
    }

    getZonedMilliseconds = (time: number | string): number => {
        if (!time) {
            return 0;
        }
        return typeof time === 'string'
            ? Temporal.Instant.from(time).epochMilliseconds
            : Temporal.Instant.fromEpochSeconds(time).epochMilliseconds;
    };

    getScheduledDepartureTime(time: Time): number {
        if (time.realtimeDeparture && typeof time.realtimeDeparture === 'string') {
            return this.getZonedMilliseconds(time.realtimeDeparture);
        }

        if (time.scheduledDeparture && typeof time.scheduledDeparture === 'string') {
            return this.getZonedMilliseconds(time.scheduledDeparture);
        }

        if (typeof time.scheduledDeparture === 'number') {
            return this.getZonedMilliseconds(time.serviceDay + time.scheduledDeparture);
        }
    }
}
